import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell, BellOff, Send, AlertTriangle, Smartphone } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported, getPushStatus, subscribePush, unsubscribePush, sendTestPush,
} from "@/lib/push";

/**
 * Settings → Push tab.
 * Lets the user enable/disable Web Push and send themselves a test alert.
 */
export default function PushSettings() {
  const [status, setStatus] = useState({ supported: pushSupported(), permission: "default", subscribed: false });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setStatus(await getPushStatus());
  };

  useEffect(() => { refresh(); }, []);

  const onToggle = async (checked) => {
    setBusy(true);
    try {
      if (checked) {
        await subscribePush();
        toast.success("Push notifications turned on for this device.");
      } else {
        await unsubscribePush();
        toast.success("Push notifications turned off for this device.");
      }
      await refresh();
    } catch (e) {
      toast.error(e.message || "Couldn't change push setting.");
    } finally {
      setBusy(false);
    }
  };

  const onTest = async () => {
    setBusy(true);
    try {
      const res = await sendTestPush();
      toast.success(`Test push fired to ${res.sent} device${res.sent === 1 ? "" : "s"}. Check your home screen / lock screen.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Test failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!status.supported) {
    return (
      <Card className="rounded-3xl p-6 border-2" data-testid="push-card">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-bold">This browser doesn't support push notifications.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Push works on <b>Android Chrome</b>, <b>desktop Chrome / Edge</b>, <b>macOS Safari</b>,
              and <b>iPhone/iPad</b> when you've installed the app to your home screen first.
              On iOS, install the app via Help → "Install the app" then come back here.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const blocked = status.permission === "denied";

  return (
    <Card className="rounded-3xl p-6 border-2 space-y-5" data-testid="push-card">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-client/15 ring-1 ring-client/25 grid place-items-center flex-shrink-0">
          <Bell className="w-5 h-5 text-client" />
        </div>
        <div>
          <h2 className="text-lg font-display font-black tracking-tight">Push notifications</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Get a buzz on your phone or computer the instant something needs your attention —
            even when the app isn't open.
          </p>
        </div>
      </div>

      {blocked ? (
        <div className="rounded-2xl bg-rose-50 dark:bg-rose-950/30 border-2 border-rose-200/70 dark:border-rose-700/30 px-4 py-3 flex gap-3"
             data-testid="push-blocked-banner">
          <BellOff className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-bold">Notifications are blocked in this browser.</p>
            <p className="text-muted-foreground mt-0.5">
              Open your browser site settings (the lock icon next to the URL on desktop, or
              site permissions on mobile) and allow notifications, then refresh.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between rounded-2xl border-2 px-4 py-3" data-testid="push-toggle-row">
          <div>
            <Label className="text-base font-bold flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              Notifications on this device
            </Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              {status.subscribed
                ? "On — this device will get push alerts."
                : status.permission === "default"
                  ? "Tap to turn on. Your browser will ask you to allow notifications."
                  : "Off — turn on to start receiving alerts."}
            </p>
          </div>
          <Switch
            checked={status.subscribed}
            disabled={busy}
            onCheckedChange={onToggle}
            data-testid="push-toggle"
          />
        </div>
      )}

      <Button
        onClick={onTest}
        disabled={!status.subscribed || busy}
        className="rounded-full font-bold gap-1.5"
        style={{ background: "hsl(var(--client))" }}
        data-testid="push-test-btn"
      >
        <Send className="w-4 h-4" />
        Send a test
      </Button>

      <div className="text-xs text-muted-foreground pt-1 border-t -mx-6 px-6 pt-3">
        <p className="font-bold mb-1">What you'll be notified about:</p>
        <ul className="space-y-0.5 list-disc list-inside">
          <li>Workers: timesheets, vitals, incidents and other items waiting on your approval</li>
          <li>Clients: when the worker creates new items or when you approve/reject something</li>
        </ul>
      </div>
    </Card>
  );
}
